<template>
  <div id="attributes">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div
        id="searchBarSection"
        class="atb-flex-shrink"
      >
        <SearchPanel
          page="attributes"
          @search-clicked="searchAttributes"
          @reset-clicked="resetAttributes"
        />
      </div>
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="attributesListRefreshButton"
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refreshAttributes"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="attributesListCopyButton"
                    motif="tertiary"
                    :title="$t('copy')"
                    aria-label="copy button"
                    @click="openCopyForm"
                  >
                    <template #icon>
                      <span class="material-icons-outlined">file_copy</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="attributesListCreateButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="emit('create')"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="attributesListDeleteButton"
                    motif="tertiary"
                    :title="$t('delete')"
                    aria-label="delete button"
                    @click="openDeleteModal"
                  >
                    <template #icon>
                      <span class="material-icons icon">
                        delete_outline_icon
                      </span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            id="attributesListAllAttributesGrid"
            class="ag-theme-eds atb-w-full atb-h-full"
            :column-defs="columnDefs"
            :components="gridComponents"
            :row-model-type="'infinite'"
            :pagination-page-size="rowsPerPage"
            :pagination="true"
            :suppress-pagination-panel="true"
            :suppress-cell-selection="true"
            :suppress-row-click-selection="true"
            @grid-ready="onGridReady"
            :group-selects-filtered="true"
            :row-selection="'multiple'"
            :row-multi-select-with-click="true"
          />
          <Pagination
            v-model="currentPage"
            :page-count="totalPages"
            :total-rows="totalRows"
            :page-size="rowsPerPage"
            @pageClick="changePageAndRefresh"
            @pageSizeChange="changePageSizeAndRefresh"
            class="atb-bg-white"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import attributesApi from '@/app/features/ams/attributes/attributes-service';
  import { useAttributesStore } from '@/app/features/ams/attributes/attributes-store';
  import SearchPanel from '@/app/features/ams/common/SearchPanel.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import ExperianLockHeaderColumn from '@/app/shared/components/grid-cells/ExperianLockHeaderColumn.vue';
  import ExperianWhereUsedHeaderColumn from '@/app/shared/components/grid-cells/ExperianWhereUsedHeaderColumn.vue';
  import LockCell from '@/app/shared/components/grid-cells/LockCell.vue';
  import WhereUsedCell from '@/app/shared/components/grid-cells/WhereUsedCell.vue';
  import { CodeType } from '@/app/shared/enums/code-type.enum';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { SearchPanelParams } from '@/app/shared/types/params/search-panel-params.interface';
  import { getTransformedDate } from '@/app/shared/utils/date.util';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import {
    ColumnApi,
    GridApi,
    GridReadyEvent,
    IGetRowsParams,
  } from 'ag-grid-community';
  import { storeToRefs } from 'pinia';
  import { Ref, ref, toRefs, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { Attribute } from '@/app/shared/types/models/attribute.interface';
  import { usePagination } from '@/app/shared/hooks/use-pagination';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import { formatQueryParams } from '@/app/shared/utils/query-params-formatter.util';
  import {
    codedRenderer,
    systemsRenderer,
    lastUpdatedRenderer,
  } from '@/app/shared/components/grid-cells/cellRenderers';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import useCopyForm from '@/app/shared/hooks/use-copy-form';
  import { ViewCodeMetaInformation } from '@/app/shared/types/models/view-code-meta-information.interface';
  import { ListEvents } from '@/app/shared/types/list-events.interface';

  interface ListProps {
    clientId: number;
  }
  interface AttributeListEvents extends ListEvents<ViewCodeMetaInformation> {}

  const emit = defineEmits<AttributeListEvents>();
  const props = defineProps<ListProps>();
  const { t } = useI18n();
  const attributesStore = useAttributesStore();
  const alertStore = useAlertStore();

  const { requestParams, tabs, attributeMetadataList, massDeleteDependencies } =
    storeToRefs(attributesStore);
  const authStore = useAuthStore();
  const username = authStore.username;
  const { clientId } = toRefs(props);
  const route = useRoute();
  const whereUsed = t('whereUsed');
  const gridApi: Ref<GridApi | null> = ref(null);
  const columnApi: Ref<ColumnApi | null> = ref(null);
  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    attributeMetadataList.value.numberOfElements,
    attributeMetadataList.value.totalElements,
    gridApi
  );
  const { openCopyForm } = useCopyForm<
    ListEvents<ViewCodeMetaInformation>,
    ViewCodeMetaInformation
  >(gridApi, 'attributes', emit);

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const manualLock = (attributeId: number) => {
    attributesApi
      .lockAttribute(clientId.value, attributeId, false)
      .then(() => gridApi.value?.setDatasource(serverSideDatasource()))
      .then(() =>
        alertStore.showNotification(
          t('attributeLocked'),
          t('attributes'),
          Motif.SUCCESS
        )
      )
      .catch((error) => {
        displayError(error);
      });
  };

  const manualUnlock = (attributeId: number) => {
    attributesApi
      .unlockAttribute(clientId.value, attributeId, false)
      .then(() => gridApi.value?.setDatasource(serverSideDatasource()))
      .then(() =>
        alertStore.showNotification(
          t('attributeUnlocked'),
          t('attributes'),
          Motif.SUCCESS
        )
      )
      .catch((error) => {
        displayError(error);
      });
  };
  // TODO: Create an interface for this attribute or use an existing one
  const displayError = (error: any) => {
    alertStore.showNotification(
      error.errors[0].message,
      t('attributes'),
      Motif.ERROR
    );
  };

  let areAttributesOpen = () => {
    return attributesStore.tabs.length > 1;
  };

  const lockAllAttributes = async () => {
    await attributesStore.lockAllAttributes(clientId.value);
    alertStore.showNotification(
      t('allAttributesLocked'),
      t('attributes'),
      Motif.SUCCESS
    );
  };

  const unlockAllAttributes = async () => {
    await attributesStore.unlockAllAttributes(clientId.value);
    alertStore.showNotification(
      t('allAttributesUnlocked'),
      t('attributes'),
      Motif.SUCCESS
    );
  };

  const clickAttributeName = async (
    attributeId: number,
    attributeName: string
  ) => {
    // TODO: Create an interface for this attribute or use an existing one
    // TODO: Move variable declaration within the try/catch block
    let resp: any;
    try {
      resp = await attributesApi.lockAttribute(
        clientId.value,
        attributeId,
        true
      );
    } catch (error) {
      displayError(error);
    }

    try {
      attributesStore.updateAutoLockStatusForTab(attributeId, true);
      attributesStore.updateAutoLockInfoInOverviewList(
        clientId.value,
        attributeId,
        username,
        resp?.data?.data?.expiryEndTime
      );
    } catch (error) {
      displayError(error);
    }
    emit('createNewTab', {
      clientId: clientId.value,
      dirtyTab: new Map<string, boolean>(),
      id: attributeId,
      name: attributeName,
      isNew: false,
    });
    gridApi.value?.refreshCells();
  };

  const openInUseEntities = (id: number) => {
    attributesStore.fetchWhereUsedDetails(clientId.value, id).catch((error) => {
      displayError(error);
    });
  };

  const columnDefs = [
    {
      field: 'id',
      headerName: '',
      suppressMovable: true,
      width: 50,
      minWidth: 50,
      sortable: false,
      checkboxSelection: true,
      valueFormatter: () => '',
    },
    {
      field: 'shortName',
      headerName: t('attributeName'),
      suppressMovable: true,
      width: 250,
      minWidth: 250,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        onClick: clickAttributeName,
      },
    },
    {
      field: 'isInUse',
      headerName: '',
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      sortable: false,
      headerComponentFramework: ExperianWhereUsedHeaderColumn,
      cellRendererFramework: WhereUsedCell,
      cellRendererParams: {
        entity: 'Attribute',
        whereUsedInfo: openInUseEntities,
      },
    },
    {
      field: 'versionControls',
      headerName: '',
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      sortable: true,
      headerComponentFramework: ExperianLockHeaderColumn,
      headerComponentParams: {
        entity: 'attribute',
        areEntitiesOpen: areAttributesOpen,
        lockAll: lockAllAttributes,
        unlockAll: unlockAllAttributes,
      },
      cellRendererFramework: LockCell,
      cellRendererParams: {
        lock: manualLock,
        unlock: manualUnlock,
        getAutoLockStatusById: attributesStore.getAutoLockStatusById,
      },
    },
    {
      field: 'isCoded',
      headerName: '',
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      headerComponentParams: {
        template:
          '<div class="ag-cell-label-container" role="presentation">' +
          '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
          '  <div ref="eLabel" class="ag-header-cell-label justify-center items-center" role="presentation">' +
          `  <span class='material-icons-outlined'>insert_drive_file</span>` +
          '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
          '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>' +
          '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>' +
          '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>' +
          '    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
          '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
          '  </div>' +
          '</div>',
      },
      cellClass: 'atb-flex atb-items-center',
      cellRenderer: 'codedRenderer',
    },
    {
      field: 'systems',
      headerName: t('systems'),
      suppressMovable: true,
      width: 360,
      minWidth: 360,
      sortable: false,
      cellRenderer: 'systemsRenderer',
    },
    {
      field: 'lastUpdated',
      headerName: t('lastUpdated'),
      suppressMovable: true,
      width: 333,
      minWidth: 333,
      sortable: true,
      cellRenderer: 'lastUpdatedRenderer',
    },
  ];

  const gridComponents = {
    codedRenderer,
    systemsRenderer,
    lastUpdatedRenderer,
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    gridApi.value.setDatasource(serverSideDatasource());
    gridApi.value.sizeColumnsToFit();
  };

  const serverSideDatasource = () => {
    return {
      getRows: async ({
        sortModel,
        successCallback,
        failCallback,
      }: IGetRowsParams) => {
        try {
          gridApi.value?.showLoadingOverlay();
          await attributesStore.fetchAttributeMetaData(
            clientId.value,
            formatQueryParams(sortModel, {
              ...requestParams.value,
              page: currentPage.value > 0 ? currentPage.value - 1 : 0,
              size: rowsPerPage.value,
            })
          );
          if (attributeMetadataList.value.numberOfElements > 0) {
            gridApi.value?.hideOverlay();
          } else {
            gridApi.value?.showNoRowsOverlay();
          }
          calculatePagination(attributeMetadataList.value.totalElements);
          successCallback(
            attributeMetadataList.value.content || [],
            attributeMetadataList.value.numberOfElements || 0
          );
        } catch (error) {
          gridApi.value?.showNoRowsOverlay();
          failCallback();
          console.error((error as Error).message);
        }
      },
    };
  };

  const refreshAttributes = (): void => {
    gridApi.value?.purgeInfiniteCache();
  };

  const resetAttributes = (): void => {
    requestParams.value = {};
    attributesStore.updateRequestParams({
      codeType: CodeType.ATTRIBUTE,
    });
    gridApi.value?.setDatasource(serverSideDatasource());
  };

  const searchAttributes = (searchPanelParams: SearchPanelParams) => {
    attributesStore.updateRequestParams({
      name: searchPanelParams.nameSearchField
        ? searchPanelParams.nameSearchField
        : undefined,
      format: searchPanelParams.formatSelected
        ? searchPanelParams.formatSelected
        : undefined,
      stagg: searchPanelParams.experianSelected
        ? searchPanelParams.experianSelected
        : undefined,
      createdFrom: searchPanelParams.createdFromDate
        ? getTransformedDate(searchPanelParams.createdFromDate)
        : undefined,
      createdTo: searchPanelParams.createdToDate
        ? getTransformedDate(searchPanelParams.createdToDate, true)
        : undefined,
      updatedFrom: searchPanelParams.updatedFromDate
        ? getTransformedDate(searchPanelParams.updatedFromDate)
        : undefined,
      updatedTo: searchPanelParams.updatedToDate
        ? getTransformedDate(searchPanelParams.updatedToDate, true)
        : undefined,
      system: searchPanelParams.systemSelected
        ? searchPanelParams.systemSelected
        : undefined,
      updatedBy: searchPanelParams.updatedUserSelected
        ? searchPanelParams.updatedUserSelected
        : undefined,
      codeType: CodeType.ATTRIBUTE,
    });
    changePageAndRefresh(1);
  };

  const isRowSelected = () => {
    return (
      gridApi.value
        ?.getSelectedRows()
        .map((attribute: Attribute) => attribute.id).length !== 0
    );
  };

  const isAttributeOpen = () => {
    const selectedAttributes: Array<number> | undefined = gridApi.value
      ?.getSelectedRows()
      .map((attribute: Attribute) => attribute.id);
    return tabs.value
      .map((tab: TabHeader) => {
        return selectedAttributes?.find((id) => id === tab.id);
      })
      .find((id?: number) => typeof id !== 'undefined');
  };

  const openDeleteModal = async () => {
    if (!isRowSelected()) {
      return alertStore.showNotification(
        t('attributesNotSelected'),
        t('attributes'),
        Motif.ERROR
      );
    }
    if (isAttributeOpen()) {
      return alertStore.showNotification(
        t('deleteError'),
        t('attributes'),
        Motif.ERROR
      );
    }

    const selectedAttributes: Array<number> | undefined = gridApi.value
      ?.getSelectedRows()
      .map((attribute: Attribute) => attribute.id);
    await attributesStore
      .retrieveCyclicDependencies(clientId.value, selectedAttributes)
      .catch((error) => {
        alertStore.showNotification(
          error.errors[0].message,
          'Attributes',
          Motif.ERROR
        );
      });
    if (massDeleteDependencies.value?.length > 0) {
      emit('showDependency');
    } else {
      emit('delete');
    }
  };

  onMounted(async () => {
    if (route.params.tabName === whereUsed) {
      clickAttributeName(
        Number(route.params.attributeId),
        String(route.params.attributeName)
      );
    }
  });

  defineExpose({
    refreshAttributes,
    gridApi,
  });
</script>

<style scoped>
  .icon {
    width: 20px;
  }
</style>
